import { useCallback, useRef, useState } from "react";

import { HTTP_STATUS_CODE, QUERY_STATUS } from "constants/constants";
import { useUserContext } from "api/user/UserProvider";
import { withUserScopeCheck } from "utils/helper";
import { extractApiFieldErrorsFlat } from "utils/apiError";
import {
  createDeliveryNoteQuery,
  deleteDeliveryNoteQuery,
  getDeliveryNoteNumberQuery,
  getDeliveryNotesQuery,
  updateDeliveryNoteQuery,
} from "./queries";

const prependNote = (notes, note) => [note, ...notes];

const replaceNote = (notes, note) =>
  notes.map((current) => (current.id === note.id ? note : current));

const useDeliveryNotes = () => {
  const { userScope, setApiErrorResponse } = useUserContext();
  const [deliveryNotes, setDeliveryNotes] = useState([]);
  const [loadNotesStatus, setLoadNotesStatus] = useState(QUERY_STATUS.INITIAL);
  const [isSaveNotePending, setIsSaveNotePending] = useState(false);
  const [isDeleteNotePending, setIsDeleteNotePending] = useState(false);
  const notesRequestRef = useRef(0);
  const notesOrderByRef = useRef(undefined);

  const getDeliveryNotes = useCallback(async ({ orderBy } = {}) => {
    if (!userScope) return null;
    const requestId = ++notesRequestRef.current;
    const isStale = () => notesRequestRef.current !== requestId;
    notesOrderByRef.current = orderBy;
    setLoadNotesStatus(QUERY_STATUS.LOADING);
    try {
      const notes = await getDeliveryNotesQuery({ userScope, orderBy });
      if (isStale()) return null;
      if (!Array.isArray(notes)) {
        throw new TypeError("delivery notes: expected a list");
      }
      setDeliveryNotes(notes);
      setLoadNotesStatus(QUERY_STATUS.SUCCESS);
      return notes;
    } catch (e) {
      console.error("getDeliveryNotes", e);
      if (isStale()) return null;
      setDeliveryNotes([]);
      setLoadNotesStatus(QUERY_STATUS.ERROR);
      return null;
    }
  }, [userScope]);

  const mintDeliveryNoteNumber = useCallback(
    async ({ templateId }) => {
      if (!userScope) return null;
      try {
        const minted = await withUserScopeCheck({
          callback: () => getDeliveryNoteNumberQuery({ templateId, userScope }),
          scope: userScope,
        });
        return minted?.number || null;
      } catch (e) {
        console.error("mintDeliveryNoteNumber", e);
        return null;
      }
    },
    [userScope]
  );

  const submitNote = useCallback(
    async ({ label, query, applyToList }) => {
      if (!userScope) {
        console.error(`${label}: userScope is not defined`);
        return { fieldErrors: {} };
      }
      setIsSaveNotePending(true);
      try {
        const note = await withUserScopeCheck({
          callback: query,
          scope: userScope,
        });
        if (!note?.id) {
          throw new TypeError("delivery note: expected a saved note");
        }
        setDeliveryNotes((notes) => applyToList(notes, note));
        return { note };
      } catch (e) {
        console.error(label, e);
        const fieldErrors = extractApiFieldErrorsFlat(
          e?.response?.data?.["bad-request"]
        );
        if (Object.keys(fieldErrors).length) setApiErrorResponse(fieldErrors);
        return { status: e?.response?.status, fieldErrors };
      } finally {
        setIsSaveNotePending(false);
      }
    },
    [userScope, setApiErrorResponse]
  );

  const createDeliveryNote = useCallback(
    ({ data }) =>
      submitNote({
        label: "createDeliveryNote",
        query: () => createDeliveryNoteQuery({ data, userScope }),
        applyToList: prependNote,
      }),
    [submitNote, userScope]
  );

  const updateDeliveryNote = useCallback(
    ({ id, data }) =>
      submitNote({
        label: "updateDeliveryNote",
        query: () => updateDeliveryNoteQuery({ id, data, userScope }),
        applyToList: replaceNote,
      }),
    [submitNote, userScope]
  );

  const deleteDeliveryNote = useCallback(
    async ({ id }) => {
      if (!userScope) {
        console.error("deleteDeliveryNote: userScope is not defined");
        return false;
      }
      setIsDeleteNotePending(true);
      try {
        await withUserScopeCheck({
          callback: () => deleteDeliveryNoteQuery({ id, userScope }),
          scope: userScope,
        });
        setDeliveryNotes((notes) => notes.filter((note) => note.id !== id));
        return true;
      } catch (e) {
        console.error("deleteDeliveryNote", e);
        if (e?.response?.status === HTTP_STATUS_CODE.NOT_FOUND) {
          await getDeliveryNotes({ orderBy: notesOrderByRef.current });
        }
        return false;
      } finally {
        setIsDeleteNotePending(false);
      }
    },
    [userScope, getDeliveryNotes]
  );

  return {
    deliveryNotes,
    loadNotesStatus,
    isSaveNotePending,
    isDeleteNotePending,
    getDeliveryNotes,
    mintDeliveryNoteNumber,
    createDeliveryNote,
    updateDeliveryNote,
    deleteDeliveryNote,
  };
};

export default useDeliveryNotes;
